import React, { useState, useEffect } from 'react';
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Alert,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { createTouchstreamConfig, updateTouchstreamConfig } from '../api/touchstream';

function TouchstreamConfigForm({ config, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    name: '',
    fqdn: '',
    x_ts_id: '',
    bearer_token: '',
    context_type: 'STATIC',
    description: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showToken, setShowToken] = useState(false);
  const [showTsId, setShowTsId] = useState(false);

  useEffect(() => {
    if (config) {
      setFormData({
        name: config.name || '',
        fqdn: config.fqdn || '',
        x_ts_id: config.x_ts_id || '',
        bearer_token: config.bearer_token || '',
        context_type: config.context_type || 'STATIC',
        description: config.description || '',
      });
    }
  }, [config]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (error) setError('');
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      setError('Configuration name is required');
      return false;
    }
    if (!formData.fqdn.trim()) {
      setError('Touchstream FQDN is required');
      return false;
    }
    if (formData.fqdn.includes('://')) {
      setError('FQDN should not include protocol (http:// or https://)');
      return false;
    }
    if (!formData.x_ts_id.trim()) {
      setError('X-TS-ID is required');
      return false;
    }
    if (!formData.bearer_token.trim()) {
      setError('Bearer token is required');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setError('');
    setLoading(true);

    try {
      if (config?.id) {
        await updateTouchstreamConfig(config.id, formData);
      } else {
        await createTouchstreamConfig(formData);
      }
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save configuration');
    } finally {
      setLoading(false);
    }
  };

  const isEditMode = Boolean(config?.id);

  return (
    <>
      <DialogTitle>
        {isEditMode ? 'Edit Touchstream Configuration' : 'Add Touchstream Configuration'}
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Configuration Name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              fullWidth
              autoFocus
              helperText="A unique name to identify this configuration"
            />

            <TextField
              label="Touchstream FQDN"
              name="fqdn"
              value={formData.fqdn}
              onChange={handleChange}
              required
              fullWidth
              placeholder="api.touchstream.com"
              helperText="The Touchstream API domain (without http:// or https://)"
            />

            <TextField
              label="X-TS-ID"
              name="x_ts_id"
              type={showTsId ? 'text' : 'password'}
              value={formData.x_ts_id}
              onChange={handleChange}
              required
              fullWidth
              helperText="The X-TS-ID header value for authentication"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowTsId(!showTsId)}
                      edge="end"
                      size="small"
                    >
                      {showTsId ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              label="Bearer Token"
              name="bearer_token"
              type={showToken ? 'text' : 'password'}
              value={formData.bearer_token}
              onChange={handleChange}
              required
              fullWidth
              helperText="The Bearer token for Authorization header"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowToken(!showToken)}
                      edge="end"
                      size="small"
                    >
                      {showToken ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <FormControl fullWidth>
              <InputLabel>Context Type</InputLabel>
              <Select
                name="context_type"
                value={formData.context_type}
                onChange={handleChange}
                label="Context Type"
              >
                <MenuItem value="STATIC">Static (Established Streams)</MenuItem>
                <MenuItem value="DYNAMIC">Dynamic (Ephemeral Streams)</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              fullWidth
              multiline
              rows={2}
              placeholder="Optional description for this configuration"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading || !formData.name || !formData.fqdn || !formData.x_ts_id || !formData.bearer_token}
          >
            {loading ? 'Saving...' : (isEditMode ? 'Update' : 'Add Configuration')}
          </Button>
        </DialogActions>
      </form>
    </>
  );
}

export default TouchstreamConfigForm;